import { create } from 'zustand';
import { cvApi } from '../services/api/cvApi';
import { 
  transformCVForStore,
  fetchCVsWithPagination, 
  getDefaultOrdering 
} from '../utils/cvUtils';
import { DEFAULT_COMPARISON_OPERATOR } from '../constants/comparisonOperators';

// 1. Central initial state for easy reset
const initialState = {
  cvs: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  pageInfo: {
    endCursor: null,
    hasNextPage: false,
    totalCount: 0
  },
  searchTerm: '',
  currentViewType: 'all',
  orderBy: getDefaultOrdering('all').orderBy,
  orderDirection: getDefaultOrdering('all').orderDirection,
  limit: 30,
  lastEmployeeId: null,
  positionIds: null,
  linkedFilter: null, // null = all, true = linked only, false = unlinked only
  languageFilter: null, // language ID to filter by
  levelFilter: null, // level ID to filter by
  levelComparator: DEFAULT_COMPARISON_OPERATOR,
  skillsFilter: null, // array of skill IDs to filter by
  // UI State
  scrollPosition: 0,
  lastCardId: null,
  isReturningFromDetails: false,
  uiSearchInput: '',
  selectedPositions: [],
};

const useCVStoreRepository = create((set, get) => {
  // 2. Helper: param construction
  const buildParams = (overrides = {}) => {
    const state = get();
    return {
      limit: state.limit,
      cursor: overrides.cursor !== undefined ? overrides.cursor : state.pageInfo.endCursor,
      orderBy: state.orderBy,
      orderDirection: state.orderDirection,
      search: overrides.search !== undefined ? overrides.search : state.searchTerm,
      positionIds: overrides.positionIds !== undefined ? overrides.positionIds : state.positionIds,
      linkedFilter: overrides.linkedFilter !== undefined ? overrides.linkedFilter : state.linkedFilter,
      languageFilter: overrides.languageFilter !== undefined ? overrides.languageFilter : state.languageFilter,
      levelFilter: overrides.levelFilter !== undefined ? overrides.levelFilter : state.levelFilter,
      levelComparator: overrides.levelComparator !== undefined ? overrides.levelComparator : state.levelComparator,
      skillsFilter: overrides.skillsFilter !== undefined ? overrides.skillsFilter : state.skillsFilter,
      ...overrides,
    };
  };

  // 3. Generic fetcher (reset/append)
  const fetchCVs = async ({ reset = false, overrides = {} } = {}) => {
    if (get().isLoading || get().isLoadingMore) return null;

    if (reset) set({ isLoading: true, error: null });
    else set({ isLoadingMore: true, error: null });

    const params = buildParams(reset ? { ...overrides, cursor: null } : overrides);
    try {
      const response = await fetchCVsWithPagination(cvApi.getAllCVs, params);

      if (response) {
        const newCVs = reset ? response.items : [...get().cvs, ...response.items];
        const pageInfo = {
          ...response.pageInfo,
          hasNextPage: newCVs.length < response.pageInfo.totalCount,
        };
        set({
          cvs: newCVs,
          pageInfo,
          isLoading: false,
          isLoadingMore: false,
          error: null,
        });
        return response;
      }
      set({ isLoading: false, isLoadingMore: false });
      return null;
    } catch (err) {
      set({ 
        error: err.message || 'Failed to load CVs',
        isLoading: false,
        isLoadingMore: false,
      });
      return null;
    }
  };

  return {
    ...initialState,

    // View type & ordering
    setOrderingForViewType: (viewType) => {
      const defaultOrdering = getDefaultOrdering(viewType);
      set({ 
        currentViewType: viewType,
        orderBy: defaultOrdering.orderBy,
        orderDirection: defaultOrdering.orderDirection,
      });
    },

    // UI State Actions
    setScrollPosition: (position) => set({ scrollPosition: position }),
    setLastCardId: (id) => set({ lastCardId: id }),
    setIsReturningFromDetails: (flag) => set({ isReturningFromDetails: flag }),
    setUISearchInput: (input) => set({ uiSearchInput: input }),
    setSelectedPositions: (positions) => {
      set({ selectedPositions: positions });
      const positionIds = positions.length > 0 ? positions.map(pos => pos.id) : null;
      get().setPositionIds(positionIds);
    },

    // Filters
    setSearchTerm: (searchTerm, skipRefetch = false) => {
      set({ searchTerm });
      if (!skipRefetch) get().fetchCVsWithReset({ search: searchTerm });
    },
    setOrderBy: (orderBy, skipRefetch = false) => {
      set({ orderBy });
      if (!skipRefetch) get().fetchCVsWithReset({ orderBy });
    },
    setOrderDirection: (orderDirection, skipRefetch = false) => {
      set({ orderDirection });
      if (!skipRefetch) get().fetchCVsWithReset({ orderDirection });
    },
    setLimit: (limit, skipRefetch = false) => {
      set({ limit });
      if (!skipRefetch) get().fetchCVsWithReset({ limit });
    },
    setPositionIds: (positionIds, skipRefetch = false) => {
      set({ positionIds });
      if (!skipRefetch) get().fetchCVsWithReset({ positionIds });
    },
    setLinkedFilter: (linkedFilter, skipRefetch = false) => {
      set({ linkedFilter });
      if (!skipRefetch) get().fetchCVsWithReset({ linkedFilter });
    },
    setLanguageFilter: (languageFilter, skipRefetch = false) => {
      set({ languageFilter });
      if (!skipRefetch) get().fetchCVsWithReset({ languageFilter });
    },
    setLevelFilter: (levelFilter, skipRefetch = false) => {
      set({ levelFilter });
      if (!skipRefetch) get().fetchCVsWithReset({ levelFilter });
    },
    setLevelComparator: (levelComparator, skipRefetch = false) => {
      set({ levelComparator });
      if (!skipRefetch) get().fetchCVsWithReset({ levelComparator });
    },
    setSkillsFilter: (skillsFilter, skipRefetch = false) => {
      set({ skillsFilter });
      if (!skipRefetch) get().fetchCVsWithReset({ skillsFilter });
    },

    // 4. Data actions
    fetchCVsWithReset: (overrides = {}) => fetchCVs({ reset: true, overrides }),
    fetchCVsLoadMore: () => fetchCVs({ reset: false }),

    updateCV: (updatedCV) => {
      if (!updatedCV || !updatedCV.id) {
        return false;
      }
      const { cvs, pageInfo } = get();
      const index = cvs.findIndex(cv => cv.id === updatedCV.id);
      const existingCV = index !== -1 ? cvs[index] : null;
      
      const transformedCV = transformCVForStore(updatedCV, existingCV);
      
      if (index !== -1) {
        // Update existing CV
        const updatedCVs = [...cvs];
        updatedCVs[index] = transformedCV;
        set({ cvs: updatedCVs });

        return true;
      } else {
        // Add new CV and increment total count
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: pageInfo.totalCount + 1 // Increment total count for new CV
        };
        set({ 
          cvs: [transformedCV, ...cvs],
          pageInfo: updatedPageInfo
        });

        return true;
      }
    },

    // Filters reset
    resetFilters: () => {
      set({
        searchTerm: '',
        uiSearchInput: '',
        selectedPositions: [],
        positionIds: null,
        linkedFilter: null,
        languageFilter: null,
        levelFilter: null,
        levelComparator: DEFAULT_COMPARISON_OPERATOR,
        skillsFilter: null,
      });
    },

    // State reset
    resetState: () => set({
      ...initialState,
      // Optionally preserve UI state here
    }),

    // Infinite loader helper
    shouldLoadMore: (lastVisibleIndex, threshold = 10) => {
      const { cvs, pageInfo, isLoadingMore } = get();
      return !isLoadingMore && 
             pageInfo.hasNextPage && 
             lastVisibleIndex >= cvs.length - threshold;
    },

    getCVByIndex: (index) => {
      const { cvs } = get();
      return cvs[index] || null;
    },

    /**
     * Remove a CV from the repository store
     * @param {string} cvId - The ID of the CV to remove
     * @returns {boolean} Whether the removal was successful
     */
    removeCV: (cvId) => {
      if (!cvId) return false;

      const { cvs, pageInfo } = get();
      const index = cvs.findIndex(cv => cv.id === cvId);
      
      if (index !== -1) {
        const updatedCVs = cvs.filter(cv => cv.id !== cvId);
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: Math.max(0, pageInfo.totalCount - 1) // Decrement total count, but don't go below 0
        };
        set({ 
          cvs: updatedCVs,
          pageInfo: updatedPageInfo
        });

        return true;
      } else {
        return false;
      }
    },
  };
});

export default useCVStoreRepository;